import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  HostBinding,
  Inject,
  Input,
  PLATFORM_ID,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { FreelancerBreakpoints } from '@freelancer/ui/breakpoints';
import { ContainerSize } from '@freelancer/ui/container';
import { HorizontalAlignment } from '@freelancer/ui/grid';
import {
  HeadingColor,
  HeadingType,
  HeadingWeight,
} from '@freelancer/ui/heading';
import {
  PictureBackgroundImageStyles,
  PictureBgImageObjectFit,
  PictureBgImagePosition,
} from '@freelancer/ui/picture';
import { FontColor, FontType, TextSize } from '@freelancer/ui/text';
import { VideoObjectFit } from '@freelancer/ui/video';
import {
  SectionHeroAlignment,
  SectionHeroBackgroundColor,
  SectionHeroContentPosition,
  SectionHeroContentWidth,
  SectionHeroHeight,
} from './section-hero.types';

@Component({
  selector: 'fl-section-hero-logo',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroLogoComponent {}

@Component({
  selector: 'fl-section-hero-video-background',
  template: `
    <fl-video
      *ngIf="isDesktop && !error; else fallbackImage"
      class="HeroVideo"
      [flShowDesktop]="true"
      [disableControls]="true"
      [autoPlay]="true"
      [loop]="true"
      [objectFit]="VideoObjectFit.COVER"
      [src]="src"
      [loadTimeout]="loadTimeout"
      (error)="error = true"
    >
    </fl-video>
    <ng-template #fallbackImage>
      <fl-picture
        [isBackgroundImage]="true"
        [flShowDesktop]="true"
        [src]="fallbackImageSrc"
      ></fl-picture>
    </ng-template>
  `,
  styleUrls: ['./section-hero-video-background.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroVideoBackgroundComponent implements OnInit {
  VideoObjectFit = VideoObjectFit;

  isDesktop = false;
  loadTimeout = 10_000;

  /** A static background image to display when video fails */
  @Input() fallbackImageSrc: string;
  @Input() src: string;

  @HostBinding('attr.data-error') error = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isDesktop = this.breakpointObserver.isMatched(
        FreelancerBreakpoints.DESKTOP_SMALL,
      );
    }
  }
}

@Component({
  selector: 'fl-section-hero-detail-text',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroDetailTextComponent {}

@Component({
  selector: 'fl-section-hero-title',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroTitleComponent {}

@Component({
  selector: 'fl-section-hero-description',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroDescriptionComponent {}

@Component({
  selector: 'fl-section-hero-image-detail',
  template: ` <ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroImageDetailComponent {}

@Component({
  selector: 'fl-section-hero-custom-content',
  template: ` <ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroCustomContentComponent {}

@Component({
  selector: 'fl-section-hero-background-image',
  template: `
    <fl-picture
      [src]="src"
      [srcTablet]="srcTablet"
      [srcDesktop]="srcDesktop"
      [alt]="alt"
      [isBackgroundImage]="true"
      [backgroundStyles]="backgroundStyles"
    >
    </fl-picture>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroBackgroundImageComponent {
  @Input() alt: string;
  @Input() src: string;
  @Input() srcTablet?: string;
  @Input() srcDesktop?: string;
  @Input() srcLargeDesktop?: string;
  @Input() backgroundStyles?: PictureBackgroundImageStyles = {
    position: PictureBgImagePosition.CENTER_TOP,
    objectFit: PictureBgImageObjectFit.COVER,
  };
}

@Component({
  selector: 'fl-section-hero',
  template: `
    <ng-container *ngIf="sectionHeroVideoBackground">
      <ng-content select="fl-section-hero-video-background"></ng-content>
    </ng-container>
    <fl-container
      class="SectionHeroContainer"
      [size]="containerSize"
      [sizeTablet]="containerSizeTablet"
      [sizeDesktopSmall]="containerSizeDesktopSmall"
    >
      <div [flHideDesktop]="!!sectionHeroVideoBackground">
        <ng-content select="fl-section-hero-background-image"></ng-content>
      </div>
      <fl-grid
        class="SectionHeroGrid"
        [hAlign]="
          alignment == SectionHeroAlignment.CENTER
            ? HorizontalAlignment.HORIZONTAL_CENTER
            : undefined
        "
        [flAnimateOnScroll]="animateOnScroll"
        [flAnimateOnScrollBehavior]="animateOnScrollBehavior"
      >
        <fl-col
          *ngIf="sectionHeroImageDetail"
          class="SectionHeroGrid-detail"
          [col]="12"
          [colDesktopSmall]="6"
          [colTabletOrder]="2"
          [flMarginBottom]="'small'"
          [flMarginBottomTablet]="'none'"
        >
          <div class="ImageDetail">
            <ng-content select="fl-section-hero-image-detail"></ng-content>
          </div>
        </fl-col>
        <fl-col
          class="SectionHeroGrid-content"
          [attr.data-content-position]="contentPosition"
          [col]="12"
          [colTablet]="sectionHeroImageDetail ? 6 : contentWidth"
          [colTabletOrder]="1"
        >
          <div
            *ngIf="sectionHeroLogo"
            [flMarginBottom]="'xsmall'"
          >
            <ng-content select="fl-section-hero-logo"></ng-content>
          </div>
          <fl-heading
            [color]="
              backgroundColor === SectionHeroBackgroundColor.DARK
                ? HeadingColor.LIGHT
                : HeadingColor.DARK
            "
            [headingType]="HeadingType.H1"
            [size]="TextSize.XLARGE"
            [sizeTablet]="TextSize.XXLARGE"
            [sizeDesktop]="TextSize.XXXLARGE"
            [sizeDesktopXLarge]="TextSize.MARKETING_MEGA"
            [flMarginBottom]="'small'"
            [flMarginBottomDesktop]="'mid'"
          >
            <ng-content select="fl-section-hero-title"></ng-content>
          </fl-heading>
          <fl-text
            *ngIf="sectionHeroDescription"
            class="DarkText"
            [color]="
              backgroundColor === SectionHeroBackgroundColor.DARK
                ? FontColor.LIGHT
                : FontColor.INHERIT
            "
            [fontType]="FontType.PARAGRAPH"
            [size]="TextSize.MARKETING_SMALL"
            [sizeDesktop]="TextSize.MID"
            [flMarginBottom]="'small'"
            [flMarginBottomDesktop]="'mid'"
          >
            <ng-content select="fl-section-hero-description"></ng-content>
          </fl-text>
          <fl-text
            *ngIf="sectionHeroDetailText"
            class="DarkText"
            [color]="
              backgroundColor === SectionHeroBackgroundColor.DARK
                ? FontColor.LIGHT
                : FontColor.INHERIT
            "
            [fontType]="FontType.PARAGRAPH"
            [size]="TextSize.SMALL"
            [sizeTablet]="TextSize.XSMALL"
            [sizeDesktop]="TextSize.SMALL"
            [flMarginBottom]="'small'"
            [flMarginBottomDesktop]="'mid'"
          >
            <ng-content select="fl-section-hero-detail-text"></ng-content>
          </fl-text>
          <ng-content></ng-content>
        </fl-col>
        <fl-col
          *ngIf="sectionHeroCustomContent"
          class="SectionHeroGrid-content"
          [col]="12"
        >
          <ng-content select="fl-section-hero-custom-content"></ng-content>
        </fl-col>
      </fl-grid>
    </fl-container>
  `,
  styleUrls: ['./section-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroComponent {
  ContainerSize = ContainerSize;
  FontColor = FontColor;
  FontType = FontType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  HorizontalAlignment = HorizontalAlignment;
  SectionHeroAlignment = SectionHeroAlignment;
  SectionHeroBackgroundColor = SectionHeroBackgroundColor;
  SectionHeroContentPosition = SectionHeroContentPosition;
  TextSize = TextSize;

  @HostBinding('attr.data-alignment')
  @Input()
  alignment: SectionHeroAlignment = SectionHeroAlignment.LEFT;

  @HostBinding('attr.data-content-position')
  @Input()
  contentPosition: SectionHeroContentPosition =
    SectionHeroContentPosition.CENTER;

  @HostBinding('attr.data-height')
  @Input()
  height?: SectionHeroHeight;

  @HostBinding('attr.data-height-tablet')
  @Input()
  heightTablet?: SectionHeroHeight;

  @HostBinding('attr.data-height-desktop')
  @Input()
  heightDesktop?: SectionHeroHeight;

  @Input() contentWidth: SectionHeroContentWidth =
    SectionHeroContentWidth.SMALL;

  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_LARGE;
  @Input() containerSizeTablet?: ContainerSize;
  @Input() containerSizeDesktopSmall?: ContainerSize;

  @HostBinding('attr.data-background-color')
  @Input()
  backgroundColor = SectionHeroBackgroundColor.DARK;

  @Input() animateOnScroll = false;
  @Input() animateOnScrollBehavior: AnimationBehavior =
    AnimationBehavior.FADE_FROM_BOTTOM;

  @ContentChild(SectionHeroDescriptionComponent)
  sectionHeroDescription: SectionHeroDescriptionComponent;
  @ContentChild(SectionHeroDetailTextComponent)
  sectionHeroDetailText: SectionHeroDetailTextComponent;
  @ContentChild(SectionHeroLogoComponent)
  sectionHeroLogo: SectionHeroLogoComponent;
  @ContentChild(SectionHeroImageDetailComponent)
  sectionHeroImageDetail: SectionHeroImageDetailComponent;
  @ContentChild(SectionHeroCustomContentComponent)
  sectionHeroCustomContent: SectionHeroCustomContentComponent;
  @ContentChild(SectionHeroVideoBackgroundComponent)
  sectionHeroVideoBackground: SectionHeroVideoBackgroundComponent;
}
